import React, { useState, useEffect } from 'react';
import { 
    Container, Row, Col, Card, CardHeader, CardBody, 
    Table, Button, Input, Label, UncontrolledDropdown, 
    DropdownToggle, DropdownMenu, DropdownItem, Spinner, Modal, ModalHeader, ModalBody, UncontrolledCarousel 
} from 'reactstrap';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { APIClient } from '../../helpers/api_helper';
import { toast } from 'react-toastify';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import BreadCrumb from '../../Components/Common/BreadCrumb';
import UserDetailsModal from '../../Components/Common/UserDetailsModal';
import useUserDetails from '../../hooks/useUserDetails';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '');

const ReferralLogs = () => {
    document.title = "Referral Logs";
    
    const [referralLogs, setReferralLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [pagination, setPagination] = useState({
        currentPage: 1,
        limit: 10,
        totalCount: 0
    });
    const [sort, setSort] = useState({
        column: '',
        direction: 'asc' // or 'desc'
    });
    const [dateLogsModal, setDateLogsModal] = useState(false);
    const [selectedDateLogs, setSelectedDateLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const {
        userDetailsModal,
        setUserDetailsModal,
        selectedUser,
        loadingUser,
        fetchUserDetails
    } = useUserDetails();

    const fetchReferralLogs = async (page = 1) => {
        try {
            setLoading(true);
            const offset = (page - 1) * pagination.limit;
            let url = `${API_BASE_URL}/referrals?limit=${pagination.limit}&offset=${offset}`;
            
            if (searchText) {
                url += `&searchText=${encodeURIComponent(searchText)}`;
            }
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            
            if (data.data) {
                setReferralLogs(data.data.referralData);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalCount: data.data.totalCount
                }));
            }
        } catch (error) {
            console.error('Error fetching referral logs:', error);
            toast.error("Failed to fetch referral logs");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            fetchReferralLogs(1);
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [searchText]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            responseType: '',
            dateRange: []
        });
    };

    // Update the date picker styles in useEffect
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .flatpickr-calendar {
                position: fixed !important;
                top: 50% !important;
                left: 50% !important;
                transform: translate(-50%, -50%) !important;
                z-index: 99999 !important;
            }
            .dropdown-menu {
                max-height: none !important;
                overflow: visible !important;
            }
            body.flatpickr-open {
                overflow: hidden;
            }
            
            .carousel {
                background-color: #f8f9fa;
            }
            
            .carousel-item {
                height: 250px;
            }
            
            .carousel-item img {
                height: 100%;
                object-fit: contain;
                margin: auto;
            }
            
            .modal-body {
                max-height: calc(100vh - 180px);
                overflow-y: auto;
            }
            
            .table th {
                width: 40%;
            }

            .cursor-pointer {
                cursor: pointer;
            }

            .cursor-pointer:hover {
                color: #405189 !important;
                text-decoration: underline;
            }

            th .ri-sort-line {
                opacity: 0.3;
            }

            th:hover .ri-sort-line {
                opacity: 1;
            }

            .text-body {
                cursor: pointer;
                transition: color 0.2s ease;
            }
            
            .text-body:hover {
                color: #405189 !important;
                text-decoration: underline;
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    const getImageUrl = (url) => {
        if (!url) return '/images/default-avatar.png';
        if (url.startsWith('http')) return url;
        
        const baseUrl = process.env.REACT_APP_MEDIA_URL.replace(/\/$/, '');
        const imagePath = url.replace(/^\//, '');
        return `${baseUrl}/${imagePath}`;
    };

    // Add sortData function
    const sortData = (data, column, direction) => {
        if (!column) return data;

        return [...data].sort((a, b) => {
            let aValue = column.includes('.') ? 
                column.split('.').reduce((obj, key) => obj[key], a) : 
                a[column];
            let bValue = column.includes('.') ? 
                column.split('.').reduce((obj, key) => obj[key], b) : 
                b[column];

            // Handle timestamp/date sorting
            if (column === 'timestamp') {
                aValue = new Date(aValue).getTime();
                bValue = new Date(bValue).getTime();
            }

            // Handle strings
            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (direction === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    };

    // Add handleSort function
    const handleSort = (column) => {
        setSort(prev => ({
            column,
            direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Update useEffect to sort data when sort changes
    useEffect(() => {
        if (sort.column) {
            setReferralLogs(prev => sortData([...prev], sort.column, sort.direction));
        }
    }, [sort]);

    const renderPagination = () => {
        const totalPages = Math.ceil(pagination.totalCount / pagination.limit);
        
        // Function to get page numbers to show
        const getPageNumbers = () => {
            const delta = 2; // Number of pages to show before and after current page
            const range = [];
            const rangeWithDots = [];
            let l;

            for (let i = 1; i <= totalPages; i++) {
                if (i === 1 || i === totalPages || 
                    (i >= pagination.currentPage - delta && i <= pagination.currentPage + delta)) {
                    range.push(i);
                }
            }

            for (let i of range) {
                if (l) {
                    if (i - l === 2) {
                        rangeWithDots.push(l + 1);
                    } else if (i - l !== 1) {
                        rangeWithDots.push('...');
                    }
                }
                rangeWithDots.push(i);
                l = i;
            }

            return rangeWithDots;
        };

        return (
            <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
                <Col sm={4}>
                    <div className="text-muted">Showing <span className="fw-semibold">{referralLogs.length}</span> of <span className="fw-semibold">{pagination.totalCount}</span> Results</div>
                </Col>
                <Col sm={8}>
                    <ul className="pagination pagination-separated justify-content-center justify-content-sm-end mb-sm-0">
                        <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                            <Button
                                className="page-link"
                                onClick={() => fetchReferralLogs(pagination.currentPage - 1)}
                                disabled={pagination.currentPage === 1}
                            >
                                <i className="ri-arrow-left-s-line"></i>
                            </Button>
                        </li>
                        
                        {getPageNumbers().map((pageNumber, index) => (
                            <li 
                                key={index} 
                                className={`page-item ${pageNumber === pagination.currentPage ? 'active' : ''} ${pageNumber === '...' ? 'disabled' : ''}`}
                            >
                                <Button
                                    className="page-link"
                                    onClick={() => pageNumber !== '...' && fetchReferralLogs(pageNumber)}
                                    disabled={pageNumber === '...'}
                                >
                                    {pageNumber}
                                </Button>
                            </li>
                        ))}

                        <li className={`page-item ${pagination.currentPage === totalPages ? 'disabled' : ''}`}>
                            <Button
                                className="page-link"
                                onClick={() => fetchReferralLogs(pagination.currentPage + 1)}
                                disabled={pagination.currentPage === totalPages}
                            >
                                <i className="ri-arrow-right-s-line"></i>
                            </Button>
                        </li>
                    </ul>
                </Col>
            </Row>
        );
    };

    // Add this useEffect for the token badge styles
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .token-badge {
                background: linear-gradient(135deg, #22c55e 0%, #15803d 100%);
                color: white;
                padding: 8px 16px;
                border-radius: 12px;
                box-shadow: 0 2px 8px rgba(34, 197, 94, 0.2);
                text-align: center;
                min-width: 100px;
                transition: all 0.3s ease;
            }

            .token-badge:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
            }

            .token-amount {
                font-size: 18px;
                font-weight: 600;
                line-height: 1;
                margin-bottom: 2px;
            }

            .token-label {
                font-size: 11px;
                font-weight: 500;
                letter-spacing: 0.5px;
                opacity: 0.9;
            }

            .table td {
                vertical-align: middle;
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Referral Logs" pageTitle="Logs" />
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader className="border-bottom-dashed">
                                    <Row className="g-3">
                                        <Col xxl={3} lg={6}>
                                            <div className="search-box">
                                                <Input
                                                    type="text"
                                                    className="form-control search"
                                                    placeholder="Search for user..."
                                                    value={searchText}
                                                    onChange={(e) => setSearchText(e.target.value)}
                                                />
                                                <i className="ri-search-line search-icon"></i>
                                            </div>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <div className="table-responsive">
                                        <Table className="table-striped table-nowrap align-middle mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Sl No</th>
                                                    <th>Date & Time</th>
                                                    <th>Referral Code</th>
                                                    <th>Referred User</th>
                                                    <th>Referred By</th>
                                                    <th>Tokens Earned</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {referralLogs.map((log, index) => (
                                                    <tr key={log._id}>
                                                        <td>{index + 1}</td>
                                                        <td>{moment(log.createdTs).format('DD MMM YYYY, hh:mm A')}</td>
                                                        <td>
                                                            <span className="badge bg-info-subtle text-info">
                                                                {log.code}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <h5 className="fs-13 mb-1">
                                                                    <Link 
                                                                        to="#" 
                                                                        className="text-body"
                                                                        onClick={() => fetchUserDetails(log.user.referredUserId)}
                                                                    >
                                                                        {log.user.username}
                                                                    </Link>
                                                                </h5>
                                                                <p className="text-muted mb-0">{log.user.email}</p>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <h5 className="fs-13 mb-1">
                                                                    <Link 
                                                                        to="#" 
                                                                        className="text-body"
                                                                        onClick={() => fetchUserDetails(log.referredBy.referredByUserId)}
                                                                    >
                                                                        {log.referredBy.username}
                                                                    </Link>
                                                                </h5>
                                                                <p className="text-muted mb-0">{log.referredBy.email}</p>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center justify-content-center">
                                                                <div className="token-badge">
                                                                    <div className="token-amount">
                                                                        {log.referredByTokens || 0}
                                                                    </div>
                                                                    <div className="token-label">
                                                                        TOKENS
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {referralLogs.length === 0 && (
                                                    <tr>
                                                        <td colSpan="5" className="text-center py-4 text-muted">
                                                            <i className="ri-information-line fs-4 me-1 align-middle"></i>
                                                            No referral logs found
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                    {renderPagination()}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    <UserDetailsModal 
                        isOpen={userDetailsModal}
                        toggle={() => setUserDetailsModal(false)}
                        user={selectedUser}
                        loading={loadingUser}
                    />
                </Container>
            </div>
            {loading && (
                <div className="overlay">
                    <div className="spinner-container">
                        <Spinner color="primary" />
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};

export default ReferralLogs;